import { useEffect, useMemo, useState } from 'react';
import { api, vendorCopy } from './_shop-helpers';

interface VendorCardInitial {
  last4?: string;
  alias_email?: string;
  verified_at?: string | null;
}

interface VendorCardItem {
  last4?: string;
  alias_email?: string;
  verified_at?: string | null;
}

interface VendorCardBlockProps {
  vendor: string;
  initial?: VendorCardInitial;
  token?: string;
  onSaved?: (item: VendorCardItem | null) => void;
}

export default function VendorCardBlock({ vendor, initial, token, onSaved }: VendorCardBlockProps) {
  const copy = vendorCopy[vendor as keyof typeof vendorCopy] ?? { label: vendor, help: '', openUrl: '#' };
  const [last4, setLast4] = useState(initial?.last4 || '');
  const [aliasEmail, setAliasEmail] = useState(initial?.alias_email || '');
  const [verified, setVerified] = useState(Boolean(initial?.verified_at));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setLast4(initial?.last4 || '');
    setAliasEmail(initial?.alias_email || '');
    setVerified(Boolean(initial?.verified_at));
  }, [initial]);

  const cleanLast4 = useMemo(() => last4.replace(/\D/g, '').slice(-4), [last4]);
  const canVerify = cleanLast4.length === 4;

  async function saveCard(verify: boolean) {
    try {
      setSaving(true);
      setError('');
      setMessage('');
      const payload = {
        vendor,
        last4: cleanLast4,
        alias_email: aliasEmail.trim() || undefined,
        verify,
      };
      const response = await api('teacher-vendor-card-upsert', payload, token);
      const item = (response?.item as VendorCardItem | undefined) || null;
      setVerified(Boolean(item?.verified_at));
      setLast4(item?.last4 || '');
      setAliasEmail(item?.alias_email || '');
      setMessage(verify ? 'Card marked verified' : 'Saved');
      onSaved?.(item);
    } catch (err) {
      setError((err as Error).message || 'Unable to save card');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4 rounded-2xl border border-sand bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <p className="text-lg font-semibold text-cocoa">{copy.label}</p>
          {copy.help ? <p className="text-sm text-cocoa/70">{copy.help}</p> : null}
        </div>
        <a
          href={copy.openUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center justify-center rounded-full border border-sand px-3 py-1 text-sm font-medium text-cocoa transition hover:bg-shell"
        >
          Open {copy.label}
        </a>
      </div>

      <div className="space-y-4">
        <label className="block text-sm font-medium text-cocoa">
          Card last 4
          <input
            value={cleanLast4}
            onChange={(event) => {
              setLast4(event.target.value);
              setError('');
              setMessage('');
            }}
            maxLength={4}
            inputMode="numeric"
            pattern="\d*"
            placeholder="1234"
            className="mt-1 w-full rounded-xl border border-sand bg-white px-3 py-2 text-base text-cocoa focus:border-brick focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick"
          />
        </label>

        <label className="block text-sm font-medium text-cocoa">
          Purchase email (optional)
          <input
            value={aliasEmail}
            onChange={(event) => {
              setAliasEmail(event.target.value);
              setError('');
              setMessage('');
            }}
            placeholder="teacher+vendor@projecttower.org"
            className="mt-1 w-full rounded-xl border border-sand bg-white px-3 py-2 text-base text-cocoa focus:border-brick focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick"
          />
        </label>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={saving}
            onClick={() => saveCard(verified)}
            className="btn"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            disabled={saving || !canVerify}
            onClick={() => saveCard(true)}
            className="rounded-lg border border-brick bg-white px-3 py-1.5 text-sm font-semibold text-brick transition hover:bg-brick/10 disabled:cursor-not-allowed disabled:border-sand disabled:text-cocoa/60"
          >
            {verified ? 'Re-verify' : 'Mark verified'}
          </button>
          {verified ? (
            <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-800">
              Verified
            </span>
          ) : null}
        </div>

        {message ? <p className="text-sm text-emerald-700">{message}</p> : null}
        {error ? <p className="text-sm text-brick">{error}</p> : null}
      </div>
    </div>
  );
}
